import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TrendingUp, AlertCircle, CreditCard } from "lucide-react";
import PlanCard from "./misc/PlanCard";
import ModalNew from "../../components/Modal/ModalNew";
import BankTransferModal from "../payment/misc/BankTransferModal";
import AddOnsAi from "../pricing/ai/misc/AddOnsAi";
import { toast } from "sonner";
import {
  subscriptionService,
  featuresService,
} from "../../services/api/settings";
import { useOrganizationData } from "../../services/api/settings";
import {
  PRICING_PLANS,
  getPlanByTier,
  canUpgradeTo,
} from "../../constants/pricingPlans";
import { Feature } from "../../store/types/settings";
import UnsubscribeButton from "../../components/elements/UnsubscribeButton";

const UpgradeSubscription: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const { organizationData, loading: orgLoading } = useOrganizationData();
  const [billingType, setBillingType] = useState<
    "MONTHLY" | "QUARTERLY" | "YEARLY"
  >("MONTHLY");
  const [loading, setLoading] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    authorizationUrl: "",
    selectedTier: "",
  });
  const [bankTransferModal, setBankTransferModal] = useState({
    isOpen: false,
    tier: "",
  });
  const [provider, setProvider] = useState<"paystack" | "paypal">("paypal");

  const currentTier = organizationData?.subscriptionTier || "STARTER";
  const currentPlan = getPlanByTier(currentTier);

  // expiry
  const subscriptionExpiry = organizationData?.subscriptionExpiry;
  const isExpired = subscriptionExpiry
    ? new Date(subscriptionExpiry) < new Date()
    : false;

  // Filter to show only upgrade options
  const upgradePlans = PRICING_PLANS.filter((plan) =>
    canUpgradeTo(currentTier, plan.tier),
  );

  const [open, setOpen] = useState(false);
  const handleModalClose2 = () => setOpen(false);

  const [features, setFeatures] = useState<Feature[]>([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [selectedDirectAddOnIds, setSelectedDirectAddOnIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const orgId = organizationData?.id;
        if (!orgId) {
          console.error("Organization ID is required to fetch features");
          return;
        }
        const response = await featuresService.getAllAvailable(orgId);
        if (response.status === "success" && response.data) {
          setFeatures(response.data);
        }
      } catch (error) {
        console.error("Error fetching features:", error);
      }
    };
    fetchFeatures();
  }, [organizationData?.id]);

  const selectedFeatures = features.filter((f) =>
    selectedFeatureIds.includes(f.id),
  );

  const calculateTotalPrice = (plan: (typeof PRICING_PLANS)[0]): string => {
    const basePriceStr =
      billingType === "MONTHLY" ? plan.priceMonthly : plan.priceYearly;
    const basePrice = parseFloat(basePriceStr.replace(/[₦,]/g, ""));
    // Convert feature prices from kobo to naira (divide by 1000)
    const featuresTotal = selectedFeatures.reduce((sum, f) => sum + f.price, 0);

    const aiAddOnsTotal = selectedDirectAddOnIds.reduce((sum, id) => {
      const addon = features.find((a) => a.id === id);
      if (!addon) return sum;
      return sum + addon.price;
    }, 0);

    const total = basePrice + featuresTotal + aiAddOnsTotal;
    return `N${total.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleFeatureRemove = (featureId: string) => {
    setSelectedFeatureIds((prev) => prev.filter((id) => id !== featureId));
  };

  const handleUpgrade = async (
    tier: string,
    selectedProvider: "paystack" | "paypal",
  ) => {
    if (loading) return;

    setLoading(true);
    setProvider(selectedProvider);

    try {
      const result = await subscriptionService.subscribe(
        tier,
        selectedProvider,
        billingType,
        organizationData?.id,
        selectedFeatureIds.length > 0 ? selectedFeatureIds : undefined,
        undefined,
        selectedDirectAddOnIds.length > 0 ? selectedDirectAddOnIds : undefined,
      );

      if (result.status === "success" && result.data?.authorizationUrl) {
        if (selectedProvider === "paystack") {
          setOpen(true);
          // setPaymentModal({
          //   isOpen: true,
          //   authorizationUrl: result.data.authorizationUrl,
          //   selectedTier: tier,
          // });
        } else {
          window.open(result.data.authorizationUrl, "_blank");
        }
      } else {
        toast.error(`Unable to initiate ${selectedProvider} payment`);
      }
    } catch (error) {
      console.error("Upgrade error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setPaymentModal({ isOpen: false, authorizationUrl: "", selectedTier: "" });
  };

  const handleBankTransfer = (tier: string) => {
    setBankTransferModal({ isOpen: true, tier });
  };

  const handleFeatureSelectionChange = (featureIds: string[]) => {
    setSelectedFeatureIds(featureIds);
  };

  const handleDirectAddOnToggle = (id: string) => {
    setSelectedDirectAddOnIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  if (orgLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-[#7f7f7f]/10 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#7f7f7f]/10 py-10 px-3 font-abel">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-[#1976D2] to-[#0B60D6] mb-2">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-[20px] md:text-[30px] font-medium text-[#212121] mb-2">
            Upgrade Your Plan
          </h1>
          <p className="text-lg text-[#616161] max-w-2xl mx-auto">
            Unlock more features and capabilities with a higher-tier
            subscription
          </p>
        </div>

        {/* Current Plan Info */}
        {currentPlan && (
          <div className="max-w-4xl mx-auto mb-4">
            <div className="rounded-lg p-3 border bg-blue-50 border-blue-300">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg mb-2 text-blue-800">
                    Your Current Plan
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 font-medium">
                      {currentPlan.name}
                    </span>
                    <span className="text-gray-900">
                      {billingType === "MONTHLY"
                        ? currentPlan.priceMonthly
                        : currentPlan.priceYearly}
                      /
                      {billingType === "MONTHLY"
                        ? "month"
                        : billingType === "QUARTERLY"
                          ? "quarterly"
                          : "year"}
                    </span>
                  </div>
                  <div className="flex items-start justify-between pt-1">
                    <p className="mt-2 text-sm text-blue-700">
                      Choose from the upgrade options below to access more
                      features
                    </p>
                    {subscriptionExpiry && (
                      <div className="flex items-center justify-end text-[14px]">
                        {isExpired ? <div></div> : <UnsubscribeButton />}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center bg-[gray]/10 rounded-full p-1 shadow-inner relative">
            <button
              onClick={() => setBillingType("MONTHLY")}
              className={`px-8 py-3 rounded-full transition-all ${
                billingType === "MONTHLY"
                  ? "bg-white text-[#0B60D6] shadow-md"
                  : "text-[gray]/70 hover:text-[gray]"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingType("YEARLY")}
              className={`px-8 py-3 rounded-full transition-all relative ${
                billingType === "YEARLY"
                  ? "bg-[white] text-[#0B60D6] shadow-md"
                  : "text-[gray]/70 hover:text-[gray]"
              }`}
            >
              Annual
            </button>
            <span className="absolute -top-1 right-2 px-2 py-0.5 bg-primary text-white text-[8px] rounded-full">
              Save 10%
            </span>
          </div>
        </div>

        {/* Upgrade Plans */}
        {upgradePlans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upgradePlans.map((plan, index) => (
              <div key={plan.tier}>
                <PlanCard
                  {...plan}
                  billingType={billingType}
                  isUpgrade={true}
                  buttonText={`Upgrade to ${plan.name.split(" ")[0]}`}
                  isExpanded={expandedPlan === index}
                  onToggleExpand={() =>
                    setExpandedPlan(expandedPlan === index ? null : index)
                  }
                  onSelect={() => {
                    // For VIP, mailto
                    if (plan.isVip) {
                      window.location.href = "mailto:solutions@25thmail.com";
                    } else {
                      // Show payment options modal or directly call API
                      // For now, default to PayPal
                      handleUpgrade(plan.tier, "paypal");
                    }
                  }}
                  selectedFeatures={selectedFeatures}
                  totalPrice={
                    selectedFeatures.length > 0
                      ? calculateTotalPrice(plan)
                      : undefined
                  }
                  onFeatureRemove={handleFeatureRemove}
                />

                {/* Additional payment option for non-VIP plans */}
                {!plan.isVip && (
                  <div className="mt-4 bg-white rounded-lg shadow-md p-4 border border-[#EEEEEE]">
                    <p className="text-sm font-semibold text-gray-700 mb-3">
                      Choose Payment Method:
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => handleUpgrade(plan.tier, "paypal")}
                        disabled={loading}
                        className="py-2 px-3 bg-[#1976D2] text-white text-sm rounded-lg font-semibold hover:bg-[#0B60D6] transition-all disabled:opacity-50"
                      >
                        PayPal
                      </button>
                      <button
                        onClick={() => handleUpgrade(plan.tier, "paystack")}
                        disabled={loading}
                        className="py-2 px-3 bg-[#1E88E5] text-white text-sm rounded-lg font-semibold hover:bg-[#1976D2] transition-all disabled:opacity-50"
                      >
                        Paystack
                      </button>
                      <button
                        onClick={() => handleBankTransfer(plan.tier)}
                        disabled={loading}
                        className="py-2 px-3 bg-green-600 text-white text-sm rounded-lg font-semibold hover:bg-green-700 transition-all disabled:opacity-50"
                      >
                        Bank
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 border border-[#EEEEEE]">
              <h3 className="text-2xl text-[#212121] mb-4">
                You're on the Highest Plan!
              </h3>
              <p className="text-gray-600 mb-6">
                You're currently enjoying our best plan. Thank you for your
                continued support!
              </p>
              <button
                onClick={() => navigate("/subscriptions/renew")}
                className="bg-gradient-to-r from-[#1976D2] to-[#0B60D6] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Renew Subscription
              </button>
            </div>
          </div>
        )}

        {/* Back to Renewals */}
        <div className="text-center mt-10">
          <button
            onClick={() => navigate("/subscriptions/renew")}
            className="text-[#1976D2] hover:text-[#0B60D6] font-semibold underline"
          >
            ← Or renew your current plan instead
          </button>
        </div>

        {/* Add-On Services Section */}
        <AddOnsAi
          selectedIds={selectedDirectAddOnIds}
          onToggle={handleDirectAddOnToggle}
        />
        {/* <AddOnServices
          selectedFeatureIds={selectedFeatureIds}
          onSelectionChange={handleFeatureSelectionChange}
          organizationId={organizationData?.id ?? ""}
          features={features}
          loading={loading}
        /> */}

        {/* Payment Modal */}
        <ModalNew
          isOpen={paymentModal.isOpen}
          onClose={handleModalClose}
          title="Complete Upgrade Payment"
        >
          <div className="p-6">
            {paymentModal.authorizationUrl ? (
              <div className="space-y-4">
                <p className="text-gray-700 mb-4">
                  Complete your payment to upgrade your subscription.
                </p>

                <div className="w-full h-96 border rounded-lg overflow-hidden">
                  <iframe
                    src={paymentModal.authorizationUrl}
                    className="w-full h-full"
                    title="Payment Gateway"
                    frameBorder="0"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={() =>
                      window.open(paymentModal.authorizationUrl, "_blank")
                    }
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
                  >
                    Open Payment Page
                  </button>
                  <button
                    onClick={handleModalClose}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p>Loading payment details...</p>
              </div>
            )}
          </div>
        </ModalNew>

        {/* Bank Transfer Modal */}
        {bankTransferModal.isOpen && (
          <BankTransferModal
            isOpen={bankTransferModal.isOpen}
            onClose={() => setBankTransferModal({ isOpen: false, tier: "" })}
            organizationId={organizationData?.id || ""}
            tier={bankTransferModal.tier}
            frequency={billingType}
            featureIds={selectedFeatureIds}
            directAddOnIds={selectedDirectAddOnIds}
          />
        )}

        <ModalNew
          isOpen={open}
          onClose={handleModalClose2}
          title=""
          className="max-w-[300px] mx-auto"
        >
          <div className="text-center">
            <div className="flex flex-col items-center text-center">
              <div
                className={`w-16 h-16 rounded-full bg-gradient-to-r from-[#00C3F7] to-[#0081CB] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
              >
                <CreditCard className="w-8 h-8 text-white" />
              </div>

              <h3 className="text-[18px] md:text-[30px] mb-2 text-[#212121]">
                Paystack
              </h3>

              <p className="text-gray-600 text-[16px] md:text-[21px] mb-4">
                Coming Soon
              </p>
            </div>
          </div>
        </ModalNew>
      </div>
    </div>
  );
};

export default UpgradeSubscription;
